import { Type } from "@sinclair/typebox";
import { Nullable, NullableAndOptional, SupportedCurrencySchema, } from "./utils.js";
export { REGION_REGISTRY, getRegionConfig, getTimezone, getCurrency, } from "./region_config.js";
export { extractFormPayload } from "./channel_form_payload.js";
export const Regions = ["hk", "sg"];
export const Environments = ["development", "staging", "production"];
/* User types */
export const UserStatus = {
    register: 0,
    authenticated: 1,
    active: 2,
    discontinue: 9,
    customer: 3,
    repeatedCustomer: 4,
};
export const UserSchema = Type.Object({
    _id: Type.String(),
    orgId: Type.String(),
    partnerId: Type.Optional(Type.String()),
    channelId: Type.Optional(Type.String()),
    email: Type.String(),
    name: Type.String(),
    familyName: Type.Optional(Type.String()),
    userStatus: Type.Number({ default: 0 }),
    affiliateCode: Type.Optional(Type.String()),
    passion: Type.Optional(Type.String()),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
    emailOptIn: Type.Optional(Type.Boolean({ default: true })),
    isMarketingPush: Type.Optional(Type.Boolean()),
    isMarketingEmail: Type.Optional(Type.Boolean()),
    /** W-1: the sign-up marketing consent box, and when it was answered. */
    marketingConsent: Type.Optional(Type.Boolean()),
    marketingConsentAt: Type.Optional(Type.String({ format: "date-time" })),
    firstAppLoginAt: Type.Optional(Type.String({ format: "date-time" })),
    lastLoginAt: Type.Optional(Type.String({ format: "date-time" })),
    /**
     * `false` = purchases and claims blocked by a declined AML screening. Only
     * the AML result path sets it to false and only an audited ops action
     * clears it (never a client write). Missing = allowed.
     */
    isAllowPurchase: Type.Optional(Type.Boolean()),
});
/* Auth types */
export const LoginOtpSchema = Type.Object({
    _id: Type.String(),
    otp: Type.String(),
    userId: Type.String(),
    expiresIn: Type.Number(),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
});
export const RefreshTokenSchema = Type.Object({
    _id: Type.String(),
    isActive: Type.Boolean(),
    uid: NullableAndOptional(Type.String()),
    email: NullableAndOptional(Type.String()),
    device: NullableAndOptional(Type.String()),
    actionDeactivate: NullableAndOptional(Type.String()),
    updatedAt: Type.String({ format: "date-time" }),
    createdAt: Type.String({ format: "date-time" }),
});
/* Item types */
export const InsureStatus = {
    insured: 1,
    uninsured: 0,
};
/* Notification types */
export const NotificationType = {
    feedback: 2,
    update: 0,
    offer: 1,
    reminder: 3,
};
export const NotificationActionButtonType = {
    goToLink: 0,
    goToInAppPage: 1,
};
export const NotificationStatus = {
    draft: 0,
    scheduled: 1,
    sent: 2,
    archived: 3,
};
export const InAppNotificationDestination = {
    vaultPage: "vaultPage",
    addTripPage: "addTripPage",
    listPolicyPage: "listPolicyPage",
    addWatchPage: "browseItemDatabasePage/4",
    addCameraPage: "browseItemDatabasePage/5",
    addLaptopPage: "browseItemDatabasePage/2",
    addJewelleryPage: "browseItemDatabasePage/6",
    addBagPage: "browseItemDatabasePage/3",
};
export const CloudMessageSchema = Type.Object({
    title: Type.String(),
    text: Type.String(),
    name: Type.Optional(Type.String()),
});
export const InAppNotificationSchema = Type.Object({
    title: Type.String(),
    type: Type.Union([
        Type.Literal(NotificationType.feedback),
        Type.Literal(NotificationType.update),
        Type.Literal(NotificationType.offer),
        Type.Literal(NotificationType.reminder),
    ], { default: NotificationType.update }),
    content: Type.String(),
    image: Type.Optional(Type.String()),
    actionButton: Type.Boolean({ default: false }),
    actionButtonTitle: Type.Optional(Type.String()),
    actionButtonType: Type.Union([
        Type.Literal(NotificationActionButtonType.goToLink),
        Type.Literal(NotificationActionButtonType.goToInAppPage),
    ]),
    actionButtonDestination: Type.Optional(Type.String()),
});
export const NotificationSchema = Type.Object({
    _id: Type.String(),
    cloudMessage: Type.Optional(CloudMessageSchema),
    inAppNotification: InAppNotificationSchema,
    status: Type.Union([
        Type.Literal(NotificationStatus.draft),
        Type.Literal(NotificationStatus.scheduled),
        Type.Literal(NotificationStatus.sent),
        Type.Literal(NotificationStatus.archived),
    ], { default: NotificationStatus.draft }),
    targetAllUsers: Type.Boolean({ default: false }),
    targetUsers: Type.Optional(Type.Array(Type.String())),
    isSchedule: Type.Optional(Type.Boolean()),
    scheduleTime: Type.Optional(Type.String({ format: "date-time" })),
    sentAt: Type.Optional(Type.String({ format: "date-time" })),
    createdAt: Type.Optional(Type.String({ format: "date-time" })),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
export const UserNotificationSchema = Type.Object({
    _id: Type.String(),
    user: Type.Union([Type.String(), UserSchema]),
    notification: Type.String(),
    viewed: Type.Boolean({ default: false }),
    data: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
export const FcmTokenSchema = Type.Object({
    _id: Type.String(),
    token: Type.Optional(Type.String()),
    userId: Type.Optional(Type.String()),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
});
export const AffiliateSchema = Type.Object({
    _id: Type.String(),
    name: Type.String(),
    email: Type.Optional(Type.String()),
    phoneNumber: Type.Optional(Type.String()),
    address: Type.Optional(Type.String()),
    type: Type.String(),
});
export const AffiliateCodeSchema = Type.Object({
    _id: Type.String(),
    code: Type.String(),
    affiliate: Type.String(),
    usageCount: Type.Number({ default: 0 }),
    link: Type.String(),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
});
export const DiscountTypeSchema = Type.Union([
    Type.Literal("PERCENTAGE"),
    Type.Literal("FIXED"),
]);
export const NumericRuleConditionSchema = Type.Object({
    eq: Type.Optional(Type.Number()),
    lt: Type.Optional(Type.Number()),
    lte: Type.Optional(Type.Number()),
    gt: Type.Optional(Type.Number()),
    gte: Type.Optional(Type.Number()),
});
export const StringRuleConditionSchema = Type.Object({
    eq: Type.Optional(Type.String()),
});
export const DiscountRuleSchema = Type.Union([
    Type.Object({
        type: Type.Literal("policy_duration"),
        op: Type.Optional(Type.Union([Type.Literal("AND"), Type.Literal("OR")])),
        conditions: Type.Array(NumericRuleConditionSchema),
    }),
    Type.Object({
        type: Type.Literal("asset_type"),
        op: Type.Optional(Type.Union([Type.Literal("AND"), Type.Literal("OR")])),
        conditions: Type.Array(StringRuleConditionSchema),
    }),
    Type.Object({
        type: Type.Literal("purchase_recency"),
        op: Type.Optional(Type.Union([Type.Literal("AND"), Type.Literal("OR")])),
        conditions: Type.Array(NumericRuleConditionSchema),
    }),
    Type.Object({
        type: Type.Literal("partner_item"),
        op: Type.Optional(Type.Union([Type.Literal("AND"), Type.Literal("OR")])),
        conditions: Type.Array(StringRuleConditionSchema),
    }),
    // Restricts a discount to a policy line (PolicyType: "item" | "travel").
    // Matched against the pricing call's `policyType`; a pricing path that
    // doesn't declare its policy type FAILS this rule (fail-closed), so a
    // product-restricted discount can never leak onto an unrelated line.
    Type.Object({
        type: Type.Literal("policy_type"),
        op: Type.Optional(Type.Union([Type.Literal("AND"), Type.Literal("OR")])),
        conditions: Type.Array(StringRuleConditionSchema),
    }),
]);
export const DiscountSchema = Type.Object({
    _id: Type.String(),
    discountCode: Type.String(),
    discountGroupCode: Type.Optional(Type.String()),
    underwriters: Type.Record(Type.String(), Type.Array(Type.String())),
    conditions: Type.Optional(Type.Array(DiscountRuleSchema)),
    discountType: DiscountTypeSchema,
    discountAmount: Type.Number(),
    fundingTechnicalPct: Type.Number(),
    fundingRetailerPct: Type.Number(),
    fundingMarketingPct: Type.Number(),
    fundingIntroducerPct: Type.Number(),
    fundingPartnerPct: Type.Number(),
    autoApply: Type.Boolean(),
    orderIndex: Type.Number(),
    // Replaces fields from old DiscountCodeSchema
    active: Type.Boolean(),
    createdAt: Type.String({ format: "date-time" }),
    expireDate: Type.Optional(Type.String({ format: "date-time" })),
    description: Type.Optional(Type.String()),
    targetUserIds: Type.Optional(Type.Array(Type.String())),
    partnerIds: Type.Optional(Type.Array(Type.String())),
    maxUsesPerCustomer: Type.Optional(Type.Number()),
    displayName: Type.Optional(Type.String()),
    shortDescription: Type.Optional(Type.String()),
    termsAndConditionsText: Type.Optional(Type.String()),
});
export const AppliedDiscountSchema = Type.Object({
    id: Type.String(), // from the original Discount config
    code: Type.String(),
    // The original configuration of the discount
    amount: Type.Number(),
    type: Type.Union([Type.Literal("PERCENTAGE"), Type.Literal("FIXED")]),
    // The calculated effective values
    netAmount: Type.Number(), // actual discount value in currency
    runningTotalAfter: Type.Number(), // (tech + pool) after this discount is funded
    orderIndex: Type.Number(),
    autoApply: Type.Boolean(),
    // How this specific discount was funded
    funding: Type.Object({
        technical: Type.Number(),
        poolKovernow: Type.Number(),
        poolRetailer: Type.Number(),
        poolMarketing: Type.Number(),
        poolIntroducer: Type.Number(),
        poolPartner: Type.Number(),
    }),
});
/**
 * A lead captured by an external marketing surface (e.g. the booth "heist"
 * mini-game) before the visitor has a KoverNow account. Keyed on a
 * lowercased email. When someone later registers with that email we mint a
 * one-time signup discount for the new user and flip `redeemed`.
 */
export const GameLeadSchema = Type.Object({
    _id: Type.String(),
    email: Type.String({ format: "email" }),
    source: Type.String(), // e.g. "heist_game"
    haulUsd: Type.Optional(Type.Number()),
    quoteUsd: Type.Optional(Type.Number()),
    capturedAt: Type.String({ format: "date-time" }),
    redeemed: Type.Boolean(),
    redeemedByUserId: Type.Optional(Type.String()),
    redeemedAt: Type.Optional(Type.String({ format: "date-time" })),
});
export const DiscountReasonCode = {
    EXPIRED: "EXPIRED",
    WRONG_ASSET_TYPE: "WRONG_ASSET_TYPE",
    WRONG_PARTNER: "WRONG_PARTNER",
    WRONG_UNDERWRITER: "WRONG_UNDERWRITER",
    NOT_ELIGIBLE: "NOT_ELIGIBLE",
    MAX_USES_REACHED: "MAX_USES_REACHED",
    WRONG_POLICY_DURATION: "WRONG_POLICY_DURATION",
    BETTER_GROUP_OFFER: "BETTER_GROUP_OFFER",
    PURCHASE_TOO_OLD: "PURCHASE_TOO_OLD",
    NOT_PARTNER_ITEM: "NOT_PARTNER_ITEM",
    WRONG_POLICY_TYPE: "WRONG_POLICY_TYPE",
};
export const ClassifiedDiscountSchema = Type.Object({
    // All fields from DiscountSchema
    _id: Type.String(),
    discountCode: Type.String(),
    discountGroupCode: Type.Optional(Type.String()),
    underwriters: Type.Record(Type.String(), Type.Array(Type.String())),
    conditions: Type.Optional(Type.Array(DiscountRuleSchema)),
    discountType: DiscountTypeSchema,
    discountAmount: Type.Number(),
    fundingTechnicalPct: Type.Number(),
    fundingRetailerPct: Type.Number(),
    fundingMarketingPct: Type.Number(),
    fundingIntroducerPct: Type.Number(),
    fundingPartnerPct: Type.Number(),
    autoApply: Type.Boolean(),
    orderIndex: Type.Number(),
    active: Type.Boolean(),
    createdAt: Type.String({ format: "date-time" }),
    expireDate: Type.Optional(Type.String({ format: "date-time" })),
    description: Type.Optional(Type.String()),
    targetUserIds: Type.Optional(Type.Array(Type.String())),
    partnerIds: Type.Optional(Type.Array(Type.String())),
    maxUsesPerCustomer: Type.Optional(Type.Number()),
    displayName: Type.Optional(Type.String()),
    shortDescription: Type.Optional(Type.String()),
    termsAndConditionsText: Type.Optional(Type.String()),
    // Classification-specific fields
    savingsAmount: Type.Number(),
    isBestOffer: Type.Boolean(),
    reasonCode: Type.Optional(Type.String()),
    reasonText: Type.Optional(Type.String()),
});
export const ClassifiedDiscountsResultSchema = Type.Object({
    applicable: Type.Array(ClassifiedDiscountSchema),
    notApplicable: Type.Array(ClassifiedDiscountSchema),
});
export const CounterSchema = Type.Object({
    _id: Type.String(),
    count: Type.Number(),
});
/* Common */
export const BaseResponseSchema = (schema) => Type.Object({
    data: schema,
    message: Type.String(),
});
export const NoDataResponseSchema = Type.Object({
    message: Type.String(),
});
/** @deprecated Use Rfc7807ErrorSchema instead */
export const BaseResponseErrorSchema = Type.Object({
    error: Type.String(),
    message: Type.String(),
});
export const RecoveryActionSchema = Type.Object({
    action: Type.String(),
    deepLink: Type.Optional(Type.String()),
    delayMs: Type.Optional(Type.Number()),
    fields: Type.Optional(Type.Array(Type.String())),
});
export const Rfc7807ErrorSchema = Type.Object({
    type: Type.String(),
    title: Type.String(),
    status: Type.Number(),
    detail: Type.String(),
    instance: Type.String(),
    correlationId: Type.String(),
    errorCode: Type.String(),
    recoveryAction: Type.Optional(RecoveryActionSchema),
});
export const PageInfoSchema = Type.Object({
    hasNextPage: Type.Boolean(),
    page: Type.Number(),
    limit: Type.Number(),
    total: Type.Number(),
    totalPage: Type.Number(),
});
export const PaginatedBaseReponseSchema = (schema) => Type.Object({
    data: Type.Optional(Type.Object({
        nodes: Type.Array(schema),
        pageInfo: PageInfoSchema,
    })),
    message: Type.String(),
});
/* Stripe */
export const StripeCardWallet = Type.Object({
    type: Type.Optional(Type.String()),
});
export const StripeCardSchema = Type.Object({
    brand: Type.Optional(Type.String()),
    country: Type.Optional(Type.String()),
    expYear: Type.Optional(Type.Number()),
    expMonth: Type.Optional(Type.Number()),
    funding: Type.Optional(Type.String()),
    last4: Type.Optional(Type.String()),
    wallet: Type.Optional(StripeCardWallet),
});
export const StripePaymentMethodSchema = Type.Object({
    id: Type.String(),
    card: StripeCardSchema,
});
/* User private */
export const UserPrivateSchema = Type.Object({
    _id: Type.String(),
    gender: Type.Optional(Type.String()),
    dob: Type.Optional(Type.String()),
    issueDate: Type.Optional(Type.String()),
    lastName: Type.Optional(Type.String()),
    firstName: Type.Optional(Type.String()),
    fullName: Type.Optional(Type.String()),
    documentNumber: Type.Optional(Type.String()),
    defaultPaymentMethod: Type.Optional(Type.String()),
    paymentCustomerId: Type.Optional(Type.String()),
    address: Type.Optional(Type.String()),
    subscriptionRenewalDate: Type.Optional(Type.String()),
    isAuthenticated: Type.Optional(Type.Boolean()),
    /**
     * AML claim: when this user's one screening was requested (ISO). An
     * atomic claim on it stops repeated/concurrent screenings; a claim older
     * than 1 h with no final result may be taken again. Server-set only.
     */
    amlScreeningRequestedAt: Type.Optional(Type.String()),
    /**
     * The latest ShuftiPro AML reference we issued for this user (ops
     * diagnostics; ongoing monitoring reuses it). The callback replay guard
     * checks the issued format, not this field. Server-set only.
     */
    amlScreeningReference: Type.Optional(Type.String()),
    policyCert: Type.Optional(Type.String()),
    oldPolicyCert: Type.Optional(Type.Array(Type.String())),
    axaPolicyScheduleUrls: Type.Optional(Type.Array(Type.String())),
    userBasedCondition: Type.Optional(Type.String()),
    paymentCustomer: Type.Optional(Nullable(Type.Record(Type.String(), Type.String()))),
    paymentMethods: Type.Optional(Nullable(Type.Array(Type.Record(Type.String(), Type.String())))),
    defaultPayment: Type.Optional(Nullable(Type.Record(Type.String(), Type.String()))),
    //SG
    occupation: Type.Optional(Type.String()),
    ownerPrivate: Type.Optional(Type.String()),
    hbdType: Type.Optional(Type.String()),
    housingType: Type.Optional(Type.String()),
    kycEmail: Type.Optional(Type.String()),
    kycName: Type.Optional(Type.String()),
    maritalStatus: Type.Optional(Type.String()),
    mobile: Type.Optional(Type.String()),
    nationality: Type.Optional(Type.String()),
    // Nullable: legacy user rows have BSON null here (never populated during
    // signup for users who never completed SingPass) — mirrors the same
    // Nullable pattern used by paymentCustomer / paymentMethods / defaultPayment
    // above. Without this, /usersPrivate/authenticate rejects any user whose
    // cached regadd is null with "/userPrivate/regadd must be object".
    regadd: Type.Optional(Nullable(Type.Object({
        country: Type.Optional(Type.Union([
            Type.String(),
            Type.Object({ code: Type.Optional(Type.String()) }),
        ])),
        unit: Type.Optional(Type.Union([
            Type.String(),
            Type.Object({ value: Type.Optional(Type.String()) }),
        ])),
        street: Type.Optional(Type.Union([
            Type.String(),
            Type.Object({ value: Type.Optional(Type.String()) }),
        ])),
        block: Type.Optional(Type.Union([
            Type.String(),
            Type.Object({ value: Type.Optional(Type.String()) }),
        ])),
        postal: Type.Optional(Type.Union([
            Type.String(),
            Type.Object({ value: Type.Optional(Type.String()) }),
        ])),
        floor: Type.Optional(Type.Union([
            Type.String(),
            Type.Object({ value: Type.Optional(Type.String()) }),
        ])),
        building: Type.Optional(Type.Union([
            Type.String(),
            Type.Object({ value: Type.Optional(Type.String()) }),
        ])),
    }))),
    // Historical user_private records can have BSON null in these fields
    // (Mongoose `{ timestamps: true }` was added later, and records created
    // through raw insert paths never had them set). fast-json-stringify@6
    // coerces null to the JSON string "null" against a String schema, which
    // breaks mobile date parsing — so allow Null in the schema.
    createdAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    updatedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
});
export const DiscountModeSchema = Type.Union([
    Type.Literal("TechPlusPoolShared"),
    Type.Literal("PoolShared"),
    Type.Literal("KovernowOnly"),
]);
export const PricingResultSchema = Type.Object({
    currency: SupportedCurrencySchema,
    itemValue: Type.Number(),
    policyDays: Type.Number(),
    technicalPrice: Type.Number(), // pre-discount tech (after min-mode)
    commissionPool: Type.Object({
        amount: Type.Number(), // pre-discount
        amountAfterDiscount: Type.Number(), // post-discount (can be < 0 when KN negative)
        shares: Type.Object({
            retailer: Type.Number(),
            marketing: Type.Number(),
            introducer: Type.Number(),
            partner: Type.Number(),
            kovernow: Type.Number(), // pre-discount share
        }),
        effectiveRateOnGwp: Type.Number(),
        capPctOfTechnical: Type.Number(),
        termFactor: Type.Number(),
    }),
    gwpComputed: Type.Number(), // pre-discount tech + pool
    finalGwp: Type.Number(), // equals gwpComputed
    commissions: Type.Object({
        retailer: Type.Number(), // ≥ 0
        marketing: Type.Number(), // ≥ 0
        introducer: Type.Number(), // ≥ 0
        partner: Type.Number(), // ≥ 0
        kovernow: Type.Number(), // may be < 0 (loss)
        total: Type.Number(), // pool after discount (may be < 0 when KN negative)
    }),
    equilibriumItemValue: Type.Number(),
    payment: Type.Object({
        feeRate: Type.Number(),
        fixedFeeBase: Type.Number(),
        premiumTechBefore: Type.Number(),
        premiumTechAfter: Type.Number(), // ≥ 0
        discountOnTechnical: Type.Number(), // ≥ 0
        commissionPoolPreDiscount: Type.Number(),
        commissionPoolDiscount: Type.Number(), // may exceed pool when KN negative
        commissionPoolAfterDiscount: Type.Number(), // may be < 0
        sanctionsFeeBase: Type.Number(),
        kycFeeBase: Type.Number(),
        otherFlatFeesBase: Type.Number(),
        subtotalAfterDiscount: Type.Number(),
        baseInvoice: Type.Number(),
        invoiceTotal: Type.Number(),
        stripeFee: Type.Number(),
        netCashReceived: Type.Number(),
    }),
    daily: Type.Object({
        termDays: Type.Number(),
        dailyGwp: Type.Number(), // pre-discount; UI can show discounted
        dailyCharge: Type.Number(),
        proRataRefundGwp: Type.Number(),
    }),
    tax: Type.Object({
        gstRate: Type.Number(),
        gstAmount: Type.Number(),
    }),
    extras: Type.Object({
        otherFlatFeesBase: Type.Number(),
        sanctionsFeeBase: Type.Number(),
        kycFeeBase: Type.Number(),
    }),
    analytics: Type.Object({
        gwpPctOfAsset: Type.Number(),
        overallPctOfAsset: Type.Number(),
    }),
    discounts: Type.Optional(Type.Object({
        applied: Type.Array(AppliedDiscountSchema), // The full list of what was applied
        totalDiscountAmount: Type.Number(), // sum of all netAmount
    })),
    premiumCredit: Type.Optional(Type.Object({
        applied: Type.Number(),
        remaining: Type.Number(),
        generated: Type.Number(),
        minimumPremiumApplied: Type.Boolean(),
    })),
});
export const CommissionSplitSharesSchema = Type.Object({
    retailer: Type.Number(),
    marketing: Type.Number(),
    introducer: Type.Number(),
    partner: Type.Number(),
});
export const LoadingCapsSchema = Type.Object({
    minTotal: Type.Optional(Type.Number()),
    maxTotal: Type.Optional(Type.Number()),
    flipIntroducerToKovernow: Type.Optional(Type.Boolean()), // legacy no-op
});
export const PremiumModeSchema = Type.Union([
    Type.Literal("TechIsTotal"),
    Type.Literal("TechPlusCommission"),
    Type.Literal("TechPlusKovernowIsTotal"),
]);
export const MinPremiumModeSchema = Type.Union([
    Type.Literal("MinEqualsTechnical"),
    Type.Literal("MinEqualsGwp"),
]);
export const PricingRuleSchema = Type.Object({
    currency: SupportedCurrencySchema,
    // Optional: collector-category rules (art/wine/whisky/tradingCard) price the
    // item value at quote time from the insured item, so their pricing_rules
    // records carry no `itemValue`. Requiring it here 500s the list endpoint via
    // a fast-json-stringify serialization crash on those docs.
    itemValue: Type.Optional(Type.Number()),
    premiumMode: PremiumModeSchema,
    techRate: Type.Number(), // annual fraction (e.g., 0.005)
    // Optional tiered technical rate: when itemValue exceeds `techRateThreshold`,
    // `techRateAboveThreshold` is applied instead of `techRate`. Both must be
    // present to tier; absent → flat `techRate`.
    techRateThreshold: Type.Optional(Type.Number()),
    techRateAboveThreshold: Type.Optional(Type.Number()),
    // AXA watch/jewellery only: item value (in the rule's currency) above which
    // the 1.0% rate applies instead of 1.5%. Absent → the calculator's 165,000
    // default (SG, in SGD). HK sets the SGD 165,000 equivalent in HKD.
    watchJewelleryRateThreshold: Type.Optional(Type.Number()),
    maxCommissionPctOfTechnical: Type.Number(), // cap on pool as % of prorated Technical
    commissionSplits: CommissionSplitSharesSchema,
    minimumGwp: Type.Number(), // for (prorate by policyDays/days in year)
    // Fees
    feeRate: Type.Number(), // fraction (e.g., 0.034)
    fixedFeeUSD: Type.Number(), // USD fixed
    usdToHkd: Type.Number(),
    usdToSgd: Type.Number(),
    passFees: Type.Boolean(),
    // Term
    policyDays: Type.Optional(Type.Number()),
    unusedDays: Type.Optional(Type.Number()),
    // Tax & extras (USD → base in code)
    gstRate: Type.Optional(Type.Number()),
    otherFlatFeesBase: Type.Optional(Type.Number()), // USD in UI, converted here
    sanctionsFeeUSD: Type.Optional(Type.Number()),
    kycFeeUSD: Type.Optional(Type.Number()),
    // Optional absolute caps on the pool
    loadingCaps: Type.Optional(LoadingCapsSchema),
    // Minimum mode
    minPremiumMode: MinPremiumModeSchema,
    // Discount config (basis = Tech+Pool)
    // discountMode: DiscountModeSchema,
    // discountPctOnTechPlusPool: Type.Number(), // fraction (0..1)
    _id: Type.String(),
    name: Type.Optional(Type.String()),
    orgId: Type.Optional(Type.String()),
    active: Type.Optional(Type.Boolean()),
    activateDate: Type.Optional(Type.String()),
    deactivateDate: Type.Optional(Type.String()),
    createdAt: Type.Optional(Type.String({ format: "date-time" })),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
export const TransactionStatusSchema = Type.Union([
    Type.Literal("INITIATED"),
    Type.Literal("PROCESSING"),
    Type.Literal("NEED_AUTH"),
    Type.Literal("SUCCESS"),
    Type.Literal("RETRYING"),
    Type.Literal("FAILED"),
]);
export const TransactionTypeSchema = Type.Union([
    Type.Literal("ACCOUNT_TO_ACCOUNT"),
    Type.Literal("CARD_TO_ACCOUNT"),
    Type.Literal("ACCOUNT_TO_CARD"),
]);
export const PricingPlanSchema = Type.Union([
    Type.Literal("1_month"),
    Type.Literal("3_months"),
    Type.Literal("6_months"),
    Type.Literal("12_months"),
]);
/* Transaction */
export const TransactionSchema = Type.Object({
    _id: Type.String(),
    orgId: Type.String(),
    partnerId: Type.Optional(Type.String()),
    channelId: Type.Optional(Type.String()),
    pricing: PricingResultSchema,
    pricingPlan: PricingPlanSchema,
    stripeInvoiceId: Type.String(),
    stripePaymentIntentId: Type.Optional(Type.String()),
    type: TransactionTypeSchema,
    failedReason: Type.Optional(Type.Union([
        Type.Null(),
        Type.Object({
            charge: Type.Optional(Type.String()),
            code: Type.Optional(Type.String()),
            decline_code: Type.Optional(Type.String()),
            message: Type.Optional(Type.String()),
            type: Type.Optional(Type.String()),
            param: Type.Optional(Type.String()),
            payment_method_type: Type.Optional(Type.String()),
        }),
    ])),
    status: TransactionStatusSchema,
    policyId: Type.Optional(Type.String()),
    prospectId: Type.Optional(Type.String()),
    userId: Type.Optional(Type.String()),
    invoiceId: Type.Optional(Type.String()),
    quotationId: Type.Optional(Type.String()),
    clientSecret: Type.Optional(Type.String()),
    paymentMethod: Type.Optional(Type.String()),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
});
export const QuotationStatusSchema = Type.Union([
    Type.Literal("REQUESTED"),
    Type.Literal("PURCHASED"),
    Type.Literal("OFFERED"),
]);
export const QuotationSchema = Type.Object({
    _id: Type.String(),
    orgId: Type.String(),
    partnerId: Type.Optional(Type.String()),
    channelId: Type.Optional(Type.String()),
    pricingId: Type.String(),
    prospectId: Type.Optional(Type.String()),
    pricing: PricingResultSchema,
    plan: PricingPlanSchema,
    underwriter: Type.String(),
    appliedDiscounts: Type.Optional(Type.Array(AppliedDiscountSchema)),
    startDate: Type.String({ format: "date-time" }),
    endDate: Type.String({ format: "date-time" }),
    status: QuotationStatusSchema,
    isRenewal: Type.Boolean(),
    expiresAt: Type.Optional(Type.String({ format: "date-time" })),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
    discountTermsAcceptedAt: Type.Optional(Type.String({ format: "date-time" })),
    discountTermsVersion: Type.Optional(Type.String()),
    // Set by POST /quotations on every quotation a client writes. Purchase
    // reprices these server-side; extension accepts only server-issued offers.
    clientSubmitted: Type.Optional(Type.Boolean()),
    // Display only: what renewing this plan would charge, priced by the server
    // (POST /pricing and POST /quotations). `date` is endDate + 1 day. Never
    // charged from; renewal reprices at the time.
    renewal: Type.Optional(Type.Object({
        date: Type.String({ format: "date-time" }),
        amount: Type.Number(),
    })),
});
/* Policy */
export const PolicyTypeSchema = Type.Union([
    Type.Literal("item"),
    Type.Literal("travel"),
]);
export const PolicySchema = Type.Object({
    _id: Type.String(),
    policyType: PolicyTypeSchema,
    orgId: Type.String(),
    partnerId: Type.Optional(Type.String()),
    channelId: Type.Optional(Type.String()),
    parentId: Type.Optional(Type.String()),
    activeClaim: Type.Optional(Type.Boolean()),
    claimId: Type.Optional(Type.String()),
    active: Type.Boolean(),
    latestInvoice: Type.Optional(Type.String()),
    currentInsuredValue: Type.Number(),
    prospectId: Type.Optional(Type.String()), // Made optional for portfolio policies
    userId: Type.String(),
    // Known item-policy durations OR a legacy free-form code. Legacy travel
    // policies store underwriter plan codes (e.g. QBE "ST" = Single Trip) that
    // predate PricingPlanSchema and are not in its four-literal union. Without
    // the String() fallback, fast-json-stringify throws on serialization and
    // 500s any CRM read (e.g. GET /api/users/:id) that returns such a policy.
    plan: Type.Optional(Type.Union([PricingPlanSchema, Type.String()])),
    policyEndDate: Type.String({ format: "date-time" }),
    policyStartDate: Type.String({ format: "date-time" }),
    policyPurchaseDate: Type.String({ format: "date-time" }),
    policyCancelDate: Type.Optional(Type.String({ format: "date-time" })),
    policyId: Type.String(),
    policyModel: Type.Optional(Type.Union([
        // NEW FIELD
        Type.Literal("portfolio"), // For AXA: one policy for all items
        Type.Literal("per_item"), // For QBE: one policy per item
    ])),
    rollMonth: Type.Optional(Type.Number()),
    underwriter: Type.String(),
    axaPolicyScheduleUrl: Type.Optional(Type.String()),
    shouldRenew: Type.Optional(Type.Boolean()),
    // Renewal payment retry tracking: how many charge attempts have been made
    // for the current renewal cycle, and when the next auto-retry is scheduled.
    renewalRetryCount: Type.Optional(Type.Number()),
    renewalRetryScheduledAt: Type.Optional(Type.String()),
    // Per-window idempotency stamp for the expiration reminder cron. Keys
    // are the ladder labels emitted by formatTimeWindow — "1 month" /
    // "7 days" / "3 days" (dots are not valid Mongo field chars but spaces
    // are; kept human-readable to match logs and CRM display). Once a value
    // is set the daily gather cron skips re-scheduling that specific window
    // for the policy, so retries/redeploys can't double-send.
    reminderSentAt: Type.Optional(Type.Record(Type.String(), Type.String({ format: "date-time" }))),
    // Travel-specific fields
    destination: Type.Optional(Type.String()),
    destinationCountries: Type.Optional(Type.Array(Type.Object({
        name: Type.String(),
        code: Type.String(),
        region: Type.String(),
    }))),
    travelType: Type.Optional(Type.String()),
    isLegacy: Type.Optional(Type.Boolean()),
    subscriptionId: Type.Optional(Type.String()),
    subscriptionItemId: Type.Optional(Type.String()),
    // TODO(schema): upcomingQuote shape is dynamic — stored quote snapshot varies by underwriter and version. Track in epic-11
    upcomingQuote: Type.Optional(Type.Any()),
    upcomingQuoteId: Type.Optional(Type.String()),
    extensible: Type.Optional(Type.Boolean()),
    isFreemium: Type.Optional(Type.Boolean()),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
/* Invoice */
export const InvoiceQuoteSchema = Type.Object({
    prospectId: Type.String(),
    policyId: Type.Optional(Type.String()),
    itemName: Type.Optional(Type.String()),
    quoteId: Type.Optional(Type.String()),
    pricingId: Type.Optional(Type.String()),
    pricing: Type.Optional(PricingResultSchema),
});
export const TravelQuoteSchema = Type.Object({
    plan: Type.String(),
    kovernowCommission: Type.Number(),
    kovernowCommissionFactor: Type.Number(),
    partnerCommission: Type.Number(),
    premium: Type.Number(),
    gst: Type.Number(),
    stripeTransaction: Type.Number(),
    stripeSuccess: Type.Number(),
    premiumAfterStripe: Type.Number(),
});
export const InvoiceTypeSchema = Type.Union([
    Type.Literal("item"),
    Type.Literal("travel"),
]);
// Structured refund record attached to an invoice when a policy/item is
// cancelled. Stores both the formula inputs (so the figure is auditable and
// reproducible) and the outputs that feed the AXA bordereau `-REFUND` row.
// All output magnitudes are stored POSITIVE; the bordereau generator negates
// them. See docs/refunds.md for the AXA SG formula.
export const InvoiceRefundSchema = Type.Object({
    // Cancellation effective date — drives days_not_covered in the prorata arm.
    effectiveDate: Type.String({ format: "date-time" }),
    // When the refund was marked in the CRM — decides which monthly BDX the
    // `-REFUND` row lands in (refunds report in the month they're processed,
    // not the policy's original month).
    processedAt: Type.String({ format: "date-time" }),
    inputs: Type.Object({
        finalGwp: Type.Number(), // pre-discount GWP (technical + full commission)
        discount: Type.Number(),
        netGwp: Type.Number(), // finalGwp - discount
        daysNotCovered: Type.Number(),
        originalCommission: Type.Number(), // pre-discount coverholder commission
        // Post-discount commission actually received — the clawback base. Optional:
        // refunds marked before 2026-10-01 were computed off originalCommission.
        netCommission: Type.Optional(Type.Number()),
    }),
    outputs: Type.Object({
        premiumRefund: Type.Number(), // BDX AA/AC magnitude (net to underwriter)
        commissionClawback: Type.Number(), // BDX AF magnitude (off net commission)
        gst: Type.Number(), // BDX AB magnitude (0 for AXA SG)
        customerRefundTotal: Type.Number(), // BDX AM magnitude
        knClawback: Type.Number(), // BDX AN magnitude
    }),
    // True when an operator overrode the computed figures.
    manualOverride: Type.Optional(Type.Boolean()),
    reason: Type.Optional(Type.String()),
    refundedBy: Type.String(), // operator uid/email
});
export const InvoiceSchema = Type.Object({
    _id: Type.String(),
    invoiceType: InvoiceTypeSchema,
    orgId: Type.String(),
    partnerId: Type.Optional(Type.String()),
    channelId: Type.Optional(Type.String()),
    policyId: Type.String(),
    invoiceNo: Type.String(),
    userId: Type.String(),
    transactionId: Type.Optional(Type.String()),
    product: Type.Optional(Type.String()),
    currency: SupportedCurrencySchema,
    quotes: Type.Array(InvoiceQuoteSchema),
    travelQuote: Type.Optional(TravelQuoteSchema),
    totalPremium: Type.Optional(Type.Number()),
    appliedDiscounts: Type.Optional(Type.Array(AppliedDiscountSchema)),
    // TODO(schema): oldInvoice is legacy pre-normalization format — shape varies by historical invoice version. Track in epic-11
    oldInvoice: Type.Optional(Type.Any()),
    // Derived mirror of `refund` presence — kept for back-compat with existing
    // reads/filters/counts that only check the boolean.
    refunded: Type.Optional(Type.Boolean()),
    refund: Type.Optional(InvoiceRefundSchema),
    // Voided/duplicate invoice: a charge that was never a real risk (e.g. a
    // duplicate) and fully reversed. Distinct from `refunded`, which is a
    // legitimate cancellation where the underwriter still keeps its minimum
    // premium and the policy is reported (with a refund row). A voided invoice is
    // excluded ENTIRELY from the bordereau — no new-business row and no refund
    // row — so it is never shown to the underwriter, while the record itself is
    // retained for the Stripe audit trail.
    voided: Type.Optional(Type.Boolean()),
    voidReason: Type.Optional(Type.String()),
    voidedAt: Type.Optional(Type.String({ format: "date-time" })),
    voidedBy: Type.Optional(Type.String()),
    campaign: Type.Optional(Type.String()),
    sponsored: Type.Optional(Type.Boolean()),
    sponsoredBy: Type.Optional(Type.String()),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
/* Claim */
export const ClaimStatus = {
    filed: 0,
    submitted: 1,
    processing: 2,
    approved: 3,
    rejected: 4,
    paid: 5,
    cancelled: 6,
    expired: 7,
};
export const DocumentSchema = Type.Object({
    url: Type.String(),
    type: Type.String(),
    location: Type.String(),
    classification: Type.String(),
    order_index: Type.Optional(Type.Number()),
});
export const DocumentTypes = {
    INVOICE: "invoice",
    VALUATION_REPORT: "valuation_report",
    PROVENANCE_RECORDS: "provenance_records",
    CONDITION_REPORT: "condition_report",
    STORAGE_STATEMENT: "storage_statement",
    PROOF_OF_EXTERNAL_STORAGE: "proof_of_external_storage",
    SELFIE_WITH_ITEM: "selfie_with_item",
    ITEM_IMAGE: "item_image",
};
export { ArtAttributesSchema, WineAttributesSchema, WhiskyAttributesSchema, TradingCardAttributesSchema, ATTRIBUTES_SCHEMAS, } from "./prospect_attributes.js";
export const ClaimSchema = Type.Object({
    _id: Type.String(),
    orgId: Type.String(),
    partnerId: Type.Optional(Type.String()),
    channelId: Type.Optional(Type.String()),
    incidentType: Type.String(),
    incidentDate: Type.String({ format: "date-time" }),
    incidentLocation: Type.String(),
    incidentDescription: Type.String(),
    amountClaimed: Type.Number(),
    currency: Type.Optional(SupportedCurrencySchema),
    damageItemImages: Type.Array(DocumentSchema),
    policeReportImages: Type.Array(DocumentSchema),
    repairQuotationImages: Type.Array(DocumentSchema),
    proofOfPurchaseImages: Type.Array(DocumentSchema),
    accountHolder: Type.String(),
    bankCode: Type.String(),
    branchCode: Type.String(),
    accountNumber: Type.String(),
    name: Type.String(),
    email: Type.String(),
    phoneNumber: Type.String(),
    item: Type.Optional(Type.String()),
    user: Type.Optional(Type.String()),
    policyNo: Type.String(),
    underwriter: Type.String(),
    claimId: Type.String(),
    status: Type.Number(),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
/*User Compliance */
export const UserComplianceSchema = Type.Object({
    _id: Type.String(),
    uid: Type.String(),
    refId: Type.String(),
    passed: Type.Boolean(),
    // TODO(schema): report shape is dynamic — varies by third-party AML screening provider. Track in epic-11
    report: Type.Any(),
    /**
     * sha256 of the result-relevant fields (event, verification_result, sorted
     * matches). Reports dedupe on `{refId, report.event, contentHash}`. Missing
     * on rows stored before it existed.
     */
    contentHash: Type.Optional(Type.String()),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
});
/* KYC verification (W-8, HK ShuftiPro) */
// One record per server-created ShuftiPro verification, keyed by our random
// `reference` (KNKYC_<uuid>, no uid/PII). Only the signed callback (or the
// `latest` reconcile through the same transition) moves it off `pending`.
// Never holds images, proof URLs or identity data.
export const KycVerificationStatusSchema = Type.Union([
    Type.Literal("pending"),
    Type.Literal("accepted"),
    Type.Literal("declined"),
    Type.Literal("cancelled"),
    Type.Literal("timeout"),
    Type.Literal("invalid"),
]);
export const KycVerificationSchema = Type.Object({
    _id: Type.String(),
    reference: Type.String(),
    uid: Type.String(),
    provider: Type.Literal("shuftipro"),
    status: KycVerificationStatusSchema,
    declinedReason: Type.Optional(Type.String()),
    declinedCodes: Type.Optional(Type.Array(Type.String())),
    /** Last reconcile `POST /status` attempt (rate limit: once a minute). */
    lastStatusCheckAt: Type.Optional(Type.String({ format: "date-time" })),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
export const PartnersSchema = Type.Object({
    _id: Type.String(),
    partnerId: Type.String(),
    organisationRef: Type.String(),
    name: Type.String(),
    country: Type.String(),
    coverages: Type.Record(Type.String(), Type.Record(Type.String(), Type.String())),
    createdAt: Type.Optional(Type.String()),
    updatedAt: Type.Optional(Type.String()),
});
export const ChannelSchema = Type.Object({
    _id: Type.String(),
    channelId: Type.String(),
    organisationRef: Type.String(),
    partnerRef: Type.String(),
    name: Type.String(),
    country: Type.String(),
    address: Type.Optional(Type.String()),
    contactNumber: Type.Optional(Type.String()),
    campaign: Type.Optional(Type.String()),
    coverages: Type.Optional(Type.Record(Type.String(), Type.Record(Type.String(), Type.String()))),
    // TODO(schema): payload values are dynamic — varies by channel configuration and integration type. Track in epic-11
    payload: Type.Record(Type.String(), Type.Any(), { default: {} }),
    createdAt: Type.Optional(Type.String()),
    updatedAt: Type.Optional(Type.String()),
});
export const ChannelFormPayloadCatalogItemSchema = Type.Object({
    type: Type.String(),
    model: Type.String(),
    price: Type.Optional(Type.Number()),
    currency: Type.Optional(Type.String()),
    assetImages: Type.Optional(Type.Array(Type.String())),
    receiptImages: Type.Optional(Type.Array(Type.String())),
    valuationImages: Type.Optional(Type.Array(Type.String())),
});
export const ChannelFormPayloadSchema = Type.Object({
    catalog: Type.Array(ChannelFormPayloadCatalogItemSchema),
    defaultCurrency: Type.Optional(Type.String()),
    freemiumPlan: PricingPlanSchema,
    branding: Type.Object({
        sponsorName: Type.String(),
        utmSource: Type.String(),
    }),
    copy: Type.Object({
        landingHeadline: Type.String(),
        landingSubtitle: Type.String(),
        freemiumLabel: Type.String(),
    }),
    tnc: Type.Object({
        partnerItems: Type.Array(Type.String()),
    }),
});
export const ProspectStatusSchema = Type.Union([
    Type.Literal("pending_otp"),
    Type.Literal("pending_sanction"),
    Type.Literal("rejected_otp"),
    Type.Literal("rejected_sanction"),
    Type.Literal("pending_underwriter_review"),
    Type.Literal("approved_underwriter_review"),
    Type.Literal("rejected_underwriter_review"),
    Type.Literal("uninsured"),
    Type.Literal("insured"),
    Type.Literal("expired"),
    Type.Literal("claiming"),
    Type.Literal("discontinued"),
]);
export const OneTimePasswordSchema = Type.Object({
    _id: Type.String(),
    identifier: Type.String(),
    otp: Type.String(),
    type: Type.String(),
    createdAt: Type.String(),
    expiresAt: Type.String(),
    usedAt: Type.Optional(Type.String()),
    /** Wrong checks so far; the code is dead after 5. */
    attempts: Type.Optional(Type.Number()),
    /** First successful reset-password check (resetWithOtp without a password). */
    verifiedAt: Type.Optional(Type.String()),
    /** When the password was reset with this OTP. */
    resetAt: Type.Optional(Type.String()),
});
export const AdminUserSchema = Type.Object({
    _id: Type.String(),
    email: Type.String(),
    name: Type.String(),
    status: Type.String(),
    aclVersion: Type.String(),
    createdAt: Type.Optional(Type.String()),
    updatedAt: Type.Optional(Type.String()),
    lastLoginAt: Type.Optional(Type.String()),
});
export const RoleBindingSchema = Type.Object({
    _id: Type.String(),
    subjectType: Type.String(),
    subjectId: Type.String(),
    roleKey: Type.String(),
    scope: Type.Optional(Type.String()),
    createdAt: Type.Optional(Type.String()),
    createdBy: Type.String(),
    expiresAt: Type.Optional(Type.String()),
    reason: Type.Optional(Type.String()),
});
export const AccessGrantSchema = Type.Object({
    _id: Type.String(),
    subjectType: Type.String(),
    subjectId: Type.String(),
    resourceType: Type.String(),
    resourceId: Type.String(),
    expiresAt: Type.Optional(Type.String()),
    reason: Type.Optional(Type.String()),
});
export const GroupMemberSchema = Type.Object({
    _id: Type.String(),
    groupId: Type.String(),
    userId: Type.String(),
    createdAt: Type.Optional(Type.String()),
    createdBy: Type.String(),
});
export const ProspectClassificationSchema = Type.Union([
    Type.Literal("watch"),
    Type.Literal("camera"),
    Type.Literal("laptop"),
    Type.Literal("jewellery"),
    Type.Literal("handbag"),
    Type.Literal("art"), // Added for AXA
    Type.Literal("wine"),
    Type.Literal("whisky"),
    Type.Literal("tradingCard"),
    Type.Literal("unknown"),
]);
export const AssetType = {
    watch: 4,
    laptop: 2,
    jewellery: 6,
    handbag: 3,
    camera: 5,
    art: 8,
    wine: 9,
    whisky: 10,
    tradingCard: 11,
};
export const OrganisationSchema = Type.Object({
    _id: Type.String(),
    organisationId: Type.String(),
    name: Type.String(),
    country: Type.String(),
    coverages: Type.Record(Type.String(), Type.Record(Type.String(), Type.String())),
    createdAt: Type.String(),
    updatedAt: Type.String(),
});
export const TravelDestination = {
    asia: 6,
    worldwide: 3,
};
export const TravelPlanType = {
    individual: "ID",
    family: "FM",
    couple: "CP",
};
export const TravelType = {
    standard: "ST",
    super: "SP",
};
export const TravelDestinationKey = Type.KeyOf(Type.Object({
    asia: Type.Number(),
    worldwide: Type.Number(),
}));
export const ProspectSchema = Type.Object({
    _id: Type.String(),
    // discriminator
    prospectType: Type.Union([
        Type.Literal("trip"),
        Type.Literal("vaultItem"),
        Type.Literal("storeItem"),
    ]),
    // common ownership/meta
    orgId: Type.Optional(Type.String()),
    partnerId: Type.Optional(Type.String()),
    channelId: Type.Optional(Type.String()),
    userId: Type.Optional(Type.String()),
    policyId: Type.Optional(Type.String()),
    universalId: Type.Optional(Type.String()),
    quoteId: Type.Optional(Type.String()),
    createdAt: Type.Optional(Type.String({ format: "date-time" })),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
    // Trip-specific
    tripStartDate: Type.Optional(Type.String({ format: "date-time" })),
    tripEndDate: Type.Optional(Type.String({ format: "date-time" })),
    destination: Type.Optional(Type.Number()),
    // destinationCountry: Type.Optional(Type.String()),
    destinationCountries: Type.Optional(Type.Array(Type.Object({
        name: Type.String(),
        code: Type.String(),
        region: Type.String(),
    }))),
    travelNo: Type.Optional(Type.Number()),
    adultNo: Type.Optional(Type.Number()),
    childNo: Type.Optional(Type.Number()),
    travelType: Type.Optional(Type.String()),
    planType: Type.Optional(Type.String()),
    travelers: Type.Optional(Type.Array(Type.Object({
        _id: Type.Optional(Type.String()),
        nric: Type.Optional(Type.String()),
        fullTimeStudent: Type.Optional(Type.Boolean()),
        dob: Type.Optional(Type.String()),
        name: Type.Optional(Type.String()),
        isChild: Type.Optional(Type.Boolean()),
        saved: Type.Optional(Type.Boolean()),
        isSelf: Type.Optional(Type.Boolean()),
        user: Type.Optional(Type.String()),
    }))),
    tripStatus: Type.Optional(Type.String()),
    travelQuote: Type.Optional(TravelQuoteSchema),
    // ItemProspect-specific
    status: Type.Optional(ProspectStatusSchema),
    expiresAt: Type.Optional(Type.String()),
    // Item-specific
    archived: Type.Optional(Type.Boolean({ default: false })),
    //Shared with Item and ItemProspect
    classification: Type.Optional(ProspectClassificationSchema),
    classId: Type.Optional(Type.Number()),
    type: Type.Optional(Type.String()),
    brand: Type.Optional(Type.String()),
    name: Type.Optional(Type.String()),
    model: Type.Optional(Type.String()),
    attributes: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    serialNumber: Type.Optional(Type.String()),
    condition: Type.Optional(Type.String()),
    notes: Type.Optional(Type.String()),
    purchasedAt: Type.Optional(Type.String()),
    valueCurrency: Type.Optional(SupportedCurrencySchema),
    valueAmount: Type.Optional(Type.Number()), //originalValue
    currentInsuredValue: Type.Optional(Type.Number()),
    documents: Type.Optional(Type.Array(DocumentSchema)),
    listAccessory: Type.Optional(Type.Array(Type.String())),
    isBrand: Type.Optional(Type.Boolean()),
    isPartnerItem: Type.Optional(Type.Boolean()),
});
export const TravelConnectionsSchema = Type.Object({
    _id: Type.String(),
    nric: Type.Optional(Type.String()),
    fullTimeStudent: Type.Optional(Type.Boolean()),
    dob: Type.Optional(Type.String()),
    name: Type.Optional(Type.String()),
    isChild: Type.Optional(Type.Boolean()),
    saved: Type.Optional(Type.Boolean()),
    isSelf: Type.Optional(Type.Boolean()),
    user: Type.Optional(Type.String()),
});
export const PremiumCreditSchema = Type.Object({
    _id: Type.String(),
    userId: Type.String(),
    underwriter: Type.String(),
    amount: Type.Number(),
    currency: SupportedCurrencySchema,
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.Optional(Type.String({ format: "date-time" })),
});
/* SingPass MyInfo types */
export const MyInfoPersonDataSchema = Type.Object({
    firstName: Type.String(),
    lastName: Type.String(),
    fullName: Type.String(),
    email: Type.String(),
    dob: Type.String(),
    nric: Type.String(),
    gender: Type.String(),
    address: Type.String(),
    mobile: Type.String(),
    occupation: Type.String(),
    maritalStatus: Type.String(),
    housingType: Type.String(),
    hdbType: Type.String(),
    ownerPrivate: Type.String(),
    nationality: Type.String(),
    genderDesc: Type.String(),
    nationalityDesc: Type.String(),
    housingTypeDesc: Type.String(),
    hdbTypeDesc: Type.String(),
});
export const SingPassSessionStatuses = [
    "pending",
    "completed",
    "consumed",
    "expired",
    "failed",
];
/* Complaint */
export { ComplaintStatusValues, ComplaintStatusSchema, ComplaintCategoryValues, ComplaintCategorySchema, SalutationSchema, ComplaintStatusHistoryEntrySchema, ComplaintAttachmentSchema, ComplaintSchema, } from "./complaint.js";
/* Drip Campaign types */
export const DripSegments = ["A", "B", "D", "E", "G"];
export const DripSegmentSchema = Type.Union(DripSegments.map((s) => Type.Literal(s)));
export const DripCampaignStatuses = ["active", "completed", "paused"];
export const DripCampaignStatusSchema = Type.Union(DripCampaignStatuses.map((s) => Type.Literal(s)));
export const DripItemSchema = Type.Object({
    prospectId: Type.String(),
    policyId: Type.String(),
    converted: Type.Boolean(),
    paidPolicyId: Type.Optional(Type.String()),
});
export const DripCampaignSchema = Type.Object({
    _id: Type.String(),
    userId: Type.String(),
    partnerId: Type.String(),
    campaign: Type.String(),
    currentSegment: DripSegmentSchema,
    currentStep: Type.Number(),
    segmentStartDate: Type.String({ format: "date-time" }),
    items: Type.Array(DripItemSchema),
    status: DripCampaignStatusSchema,
    lastEmailSentAt: Type.Optional(Type.String({ format: "date-time" })),
    nextEmailScheduledAt: Type.Optional(Type.String({ format: "date-time" })),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
});
export const DripEmailEventTypes = [
    "sent",
    "delivered",
    "opened",
    "clicked",
    "unsubscribed",
];
export const DripEmailEventTypeSchema = Type.Union(DripEmailEventTypes.map((s) => Type.Literal(s)));
export const DripEmailEventSchema = Type.Object({
    _id: Type.String(),
    emailId: Type.String(),
    event: DripEmailEventTypeSchema,
    linkId: Type.Optional(Type.String()),
    timestamp: Type.String({ format: "date-time" }),
    userAgent: Type.Optional(Type.String()),
    dripId: Type.Optional(Type.String()),
    templateId: Type.Optional(Type.String()),
    userId: Type.Optional(Type.String()),
});
/* Deep Links */
export { DeepLinkRegionSchema, DeepLinkPathValues, DeepLinkPathSchema, DeepLinkPayloadSchema, DeepLinkFingerprintSchema, ClientFingerprintSchema, DeepLinkUtmSchema, DeepLinkTokenSchema, } from "./deep_link.js";
